import { useCallback, useMemo } from 'react';
import { useTranslations } from '@/i18n/translations';
import type { CareerStage } from '@leanin/shared/views/profile';

export type CareerStageOption = { readonly value: CareerStage; readonly label: string };

// Display order for every career-stage control (Account settings, the Circle
// preferences prompt, the People filter).
export const CAREER_STAGES: readonly CareerStage[] = [
  'student',
  'early',
  'mid',
  'senior',
  'exec',
  'founder',
  'returning',
  'other',
];

const CAREER_STAGE_SET: ReadonlySet<string> = new Set(CAREER_STAGES);

export function isCareerStage(value: string): value is CareerStage {
  return CAREER_STAGE_SET.has(value);
}

// Stable per locale so memoized consumers don't recompute on every render.
export function useCareerStageLabel(): (stage: CareerStage) => string {
  const tm = useTranslations('Members');
  return useCallback((stage: CareerStage) => tm(`careerStages.${stage}`), [tm]);
}

export function useCareerStageOptions(
  stages: readonly CareerStage[] = CAREER_STAGES,
): ReadonlyArray<CareerStageOption> {
  const label = useCareerStageLabel();
  return useMemo(() => stages.map((value) => ({ value, label: label(value) })), [stages, label]);
}
